import React, { useEffect, useRef, useState } from "react";
import { Icon } from "../icons";
import { JobEscalationList, PostEscalationNotes, RaiseEscalationPanel } from "./EscalationResolution";
import { fmtDate, fmtDateTime, todayStr } from "../lib/format";
import { crewLines } from "../lib/crew";
import {
  IncludedServices,
  ReadField,
  S2Field,
  SalespersonField,
  SectionLabel,
  YesNoBadge,
  ZohoBadgeVal,
  ZohoLabel,
  makeS2Setter,
} from "./fields";

/**
 * Day-of working screen. Serves both the LD "In Progress (Local)" tab (s3p) and the
 * local move "In Progress" tab (s4) — identical in the approved prototype.
 */
export const TabInProgress = ({ job, escalations = [], actions }) => {
  const jobEsc = escalations.filter((e) => e.jobId === job.id);
  const bolSigned = !!(job.bolPickupSignedAt || job.bolDeliverySignedAt);
  const plannerCrew = crewLines(job).join("\n");
  const crewText = (job.s2Notes && job.s2Notes.dispatchedCrew) || plannerCrew;
  const set = makeS2Setter(job, actions.setS2Note);
  const [feedback, setFeedback] = useState(job.inProgressFeedback || "");
  const [remText, setRemText] = useState("");
  const [remDate, setRemDate] = useState("");

  // Transient "✓ Saved" confirmation for the button-driven saves (feedback,
  // escalation, reminder). Unlike the auto-saving note fields, these gave no
  // feedback at all, so a successful click looked like nothing happened.
  const [saved, setSaved] = useState(""); // "" | "feedback" | "reminder"
  const savedTimer = useRef(null);
  useEffect(() => () => clearTimeout(savedTimer.current), []);
  const flashSaved = (which) => {
    setSaved(which);
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaved(""), 2200);
  };

  const feedbackDirty = feedback.trim() !== "" && feedback !== (job.inProgressFeedback || "");

  return (
    <>
      <SectionLabel icon="activity">Salesperson</SectionLabel>
      <SalespersonField job={job} />
      {/* Contract and BOL under the salesperson, per the prototype (Asana 1220099429071097, 4.1). */}
      <div className="field-row">
        <div className="field">
          <label>Contract signed</label>
          <div className="val">
            <YesNoBadge value={!!job.contractSigned} />
          </div>
          <div className="liab-sub">Zoho: Contract_Signed</div>
        </div>
        <div className="field">
          <label>BOL signed</label>
          <div className="val">
            <YesNoBadge value={bolSigned} />
          </div>
          <div className="liab-sub">Zoho: BOL_Signed</div>
        </div>
      </div>

      <SectionLabel icon="truck">Logistics of the Move</SectionLabel>
      <div className="field-row">
        <ReadField label="Move date (Zoho)">{fmtDate(job.moveDate)}</ReadField>
        <div className="field">
          <label>
            <ZohoLabel text="Packing date (Zoho)" included={!!job.packingDate} />
          </label>
          <div className="val">{fmtDate(job.packingDate)}</div>
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <label>
            <ZohoLabel text="Start time (Zoho)" included={!!job.startTime} />
          </label>
          <div className="val">{job.startTime || "—"}</div>
        </div>
        <div className="field">
          <label>
            <ZohoLabel text="Unpacking date (Zoho)" included={!!job.unpackingDate} />
          </label>
          <div className="val">{fmtDate(job.unpackingDate)}</div>
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <label>
            <ZohoLabel text="Total cubic feet (Zoho)" included={!!job.totalCubicFeet} />
          </label>
          <div className="val">{job.totalCubicFeet || "—"}</div>
        </div>
        <div className="field">
          <label>
            <ZohoLabel text="Loading date (Zoho)" included={!!job.loadingDate} />
          </label>
          <div className="val">{fmtDate(job.loadingDate)}</div>
        </div>
      </div>
      <div className="field">
        <label>Disposal service</label>
        <div className="val">
          <ZohoBadgeVal included={job.disposalIncluded} />
        </div>
      </div>
      <S2Field job={job} fieldKey="logisticsNotes" label="Notes" onChange={set} />

      {/* Below the logistics, per the prototype (4.2). */}
      <SectionLabel icon="check">Special Services</SectionLabel>
      <IncludedServices job={job} />
      <S2Field job={job} fieldKey="specialServicesNotes" label="Special Service Notes" onChange={set} />

      {/* One box, filled from the planner's dispatch until the MC edits it (4.3). */}
      <SectionLabel icon="activity">Assign Crew</SectionLabel>
      <div className="field">
        <label htmlFor={`crew-${job.id}`}>Who is the dispatched crew?</label>
        <textarea
          id={`crew-${job.id}`}
          rows={Math.max(2, crewText.split("\n").length)}
          placeholder="Not assigned in the planner yet"
          defaultValue={crewText}
          onBlur={(e) => {
            if (e.target.value !== crewText) set("dispatchedCrew", e.target.value);
          }}
        />
      </div>

      <SectionLabel icon="note">Feedback from the Client</SectionLabel>
      <div className="field">
        <textarea
          rows={4}
          placeholder="Type notes from the client's feedback..."
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
        />
        <div className="toolbar-row" style={{ marginTop: 6 }}>
          <button
            className={`btn small${saved === "feedback" ? " success" : ""}`}
            type="button"
            disabled={!feedbackDirty}
            onClick={() => {
              if (!feedbackDirty) return;
              actions.setJobField(job.id, "inProgressFeedback", feedback);
              flashSaved("feedback");
            }}
          >
            <Icon name={saved === "feedback" ? "check" : "note"} />
            {saved === "feedback" ? "Saved" : "Save feedback"}
          </button>
        </div>
      </div>

      <RaiseEscalationPanel job={job} actions={actions} />

      <PostEscalationNotes job={job} escalations={jobEsc} actions={actions} />
      {/* Every escalation raised on the job, either team, under the notes (4.4). */}
      <JobEscalationList escalations={jobEsc} />

      <SectionLabel icon="bell">Reminders</SectionLabel>
      {job.reminders.length
        ? job.reminders.map((r, i) => (
            <div className="reminder-item" key={i}>
              {r.text}
              <div className="meta">{fmtDate(r.date)}</div>
            </div>
          ))
        : null}
      <div className="field-row" style={{ marginTop: 8 }}>
        <input
          type="text"
          placeholder="e.g. Call client three days before the move"
          value={remText}
          onChange={(e) => setRemText(e.target.value)}
        />
        <input type="date" value={remDate} onChange={(e) => setRemDate(e.target.value)} />
      </div>
      <div className="toolbar-row">
        <button
          className={`btn small ${saved === "reminder" ? "success" : "primary"}`}
          type="button"
          disabled={!remText.trim()}
          onClick={() => {
            if (!remText.trim()) return;
            actions.addJobReminder(job.id, remText.trim(), remDate || todayStr());
            setRemText("");
            setRemDate("");
            flashSaved("reminder");
          }}
        >
          <Icon name={saved === "reminder" ? "check" : "bell"} />
          {saved === "reminder" ? "Reminder saved" : "Save reminder"}
        </button>
      </div>

      {/* At the bottom of the page, per the prototype (4.6). */}
      <SectionLabel icon="check">In-Progress Call</SectionLabel>
      <div className="toolbar-row" style={{ marginBottom: 14 }}>
        {job.inProgressCallDone ? (
          <span className="badge resolved">
            <span className="bdot" />
            Call completed{job.inProgressCallDoneAt ? " · " + fmtDateTime(job.inProgressCallDoneAt) : ""}
          </span>
        ) : (
          <button
            className="btn success"
            type="button"
            onClick={() => actions.completeInProgressCall(job.id, feedback)}
          >
            <Icon name="check" />
            In Progress Completed
          </button>
        )}
      </div>
    </>
  );
};
